import type { Shape } from '@frame/shared';
import { isTextBearing, readTextStyle } from './textStyle';

interface Props {
  shapes: Shape[];
  selectedIds: Set<string>;
  boardId: string;
}

// Spec §5.6 — 280px panel, --bg-panel, 1px left border. Day 2 read-only;
// Day 8 turns rows into editable inputs that write back to Y.Map<id>.
export function PropertyPanel({ shapes, selectedIds, boardId }: Props) {
  const selected = shapes.filter((s) => selectedIds.has(s.id));

  return (
    <aside className="flex flex-col bg-bg-panel border-l border-border-subtle min-h-0 overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <strong className="text-13 font-medium text-fg-primary truncate">{boardId}</strong>
        <span className="font-mono text-12 tabular-nums text-fg-tertiary shrink-0">
          {shapes.length} {shapes.length === 1 ? 'shape' : 'shapes'}
        </span>
      </header>

      <div className="flex-1 overflow-y-auto">
        {selected.length === 0 && <EmptyState />}
        {selected.length === 1 && <SingleProps shape={selected[0]!} />}
        {selected.length > 1 && <MultiProps shapes={selected} />}
      </div>
    </aside>
  );
}

function EmptyState() {
  return (
    <div className="px-4 py-4 text-13 text-fg-tertiary leading-[1.5]">
      Click a shape to inspect it. Drag empty space to lasso. Hold shift to multi-select.
    </div>
  );
}

function SingleProps({ shape }: { shape: Shape }) {
  return (
    <>
      <Section title={shape.type}>
        <Field label="id" value={shape.id} mono />
        <Field label="x" value={Math.round(shape.x)} mono />
        <Field label="y" value={Math.round(shape.y)} mono />
        {'w' in shape && <Field label="w" value={Math.round(shape.w)} mono />}
        {'h' in shape && <Field label="h" value={Math.round(shape.h)} mono />}
        {shape.type === 'sticky' && <Field label="color" value={shape.color} />}
        {shape.type === 'sticky' && <Field label="text" value={shape.text} />}
        {shape.type === 'frame' && <Field label="label" value={shape.label} />}
      </Section>
      {isTextBearing(shape) && <TextStyleSummary shape={shape} />}
    </>
  );
}

// Read-only summary of the user-set textStyle overrides. Defaults are not
// shown so the panel surfaces "what's different from the per-shape default"
// at a glance. Edit affordance lives in the floating TextFormatToolbar.
function TextStyleSummary({ shape }: { shape: Shape }) {
  const style = readTextStyle(shape);
  if (!style || Object.keys(style).length === 0) {
    return (
      <Section title="text style">
        <div className="text-13 text-fg-tertiary">defaults</div>
      </Section>
    );
  }
  return (
    <Section title="text style">
      {style.fontSize !== undefined && <Field label="size" value={`${style.fontSize}px`} mono />}
      {style.bold !== undefined && <Field label="bold" value={style.bold ? 'on' : 'off'} />}
      {style.italic !== undefined && <Field label="italic" value={style.italic ? 'on' : 'off'} />}
      {style.underline !== undefined && (
        <Field label="underline" value={style.underline ? 'on' : 'off'} />
      )}
      {style.color !== undefined && <Field label="color" value={style.color} mono />}
      {style.align !== undefined && <Field label="align" value={style.align} />}
      {style.verticalAlign !== undefined && (
        <Field label="vAlign" value={style.verticalAlign} />
      )}
    </Section>
  );
}

function MultiProps({ shapes }: { shapes: Shape[] }) {
  return (
    <Section title={`${shapes.length} selected`}>
      <ul className="m-0 p-0 list-none divide-y divide-border-subtle">
        {shapes.map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between gap-2 py-1.5 text-13 text-fg-secondary"
          >
            <span className="text-fg-primary">{s.type}</span>
            <code className="font-mono text-12 text-fg-tertiary truncate">{s.id}</code>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="px-4 py-3.5 border-b border-border-subtle">
      <h3
        className="
          font-mono text-12 font-medium uppercase tracking-[0.02em]
          text-fg-tertiary mb-2
        "
      >
        {title}
      </h3>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-13">
      <span className="text-fg-secondary text-12 font-medium">{label}</span>
      <span
        className={[
          'text-fg-primary text-right truncate max-w-[60%]',
          mono ? 'font-mono tabular-nums text-12' : '',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  );
}
